import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Send } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { findCategory, SERVICE_CATEGORIES } from '@/lib/utils';
import { GlowButton } from '@/components/brand/GlowButton';
import { SectionReveal } from '@/components/brand/SectionReveal';
import { ServiceIcon } from '@/components/brand/ServiceIcon';

// --- Sub-components ---------------------------------------------------------

const ServiceNotFound = () => (
    <div className="bg-la-canvas text-la-text min-h-screen">
        <Navbar />
        <main className="pt-32 pb-24">
            <div className="max-w-3xl mx-auto px-5 text-center">
                <h1 className="font-display font-normal text-[34px] text-la-text">Service not found.</h1>
                <Link to="/#services" className="inline-flex items-center gap-2 mt-6 text-la-text2 hover:text-la-text underline-grow">
                    <ArrowLeft className="h-4 w-4" /> Back to services
                </Link>
            </div>
        </main>
        <Footer />
    </div>
);

const ServiceHero = ({ category }) => (
    <section className="relative isolate overflow-hidden min-h-[58vh] flex items-end bg-la-canvas border-b border-white/8">
        <img
            src={category.image}
            alt=""
            aria-hidden
            fetchPriority="high"
            loading="eager"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover object-center"
            style={{ filter: 'saturate(0.78) brightness(0.55) contrast(1.05)' }}
        />
        <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
                background:
                    'linear-gradient(180deg, rgba(6,8,12,0.55) 0%, rgba(6,8,12,0.78) 55%, rgba(6,8,12,0.94) 100%)',
            }}
        />
        <div className="relative z-10 w-full max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 pt-32 sm:pt-40 pb-16 sm:pb-20">
            <SectionReveal>
                <Link to="/#services" className="inline-flex items-center gap-2 text-[12px] tracking-[0.10em] uppercase text-la-text2 hover:text-la-text underline-grow">
                    <ArrowLeft className="h-3.5 w-3.5" /> All Services
                </Link>
                <span className="mt-8 inline-flex items-center justify-center h-12 w-12 rounded-la-sm border border-la-gold/35 bg-la-canvas/55 backdrop-blur-md text-la-gold">
                    <ServiceIcon name={category.icon} className="h-5 w-5" />
                </span>
                <div className="mt-6 meta-eyebrow">Service</div>
                <h1
                    data-testid="service-detail-hero"
                    className="mt-4 font-display font-normal text-[40px] sm:text-[52px] lg:text-[58px] leading-[1.05] tracking-[-0.005em] text-la-text text-balance"
                    style={{ textShadow: '0 1px 32px rgba(0,0,0,0.5)' }}
                >
                    {category.name}
                </h1>
            </SectionReveal>
        </div>
    </section>
);

const ServiceBody = ({ category }) => (
    <SectionReveal>
        <div className="max-w-2xl mt-16 space-y-5 text-[15px] sm:text-[16px] text-la-text2 leading-relaxed">
            {category.long.map((p) => (
                <p key={p.slice(0, 60)}>{p}</p>
            ))}
        </div>
    </SectionReveal>
);

const ServiceEnquiryCTA = ({ enquiryHref }) => (
    <SectionReveal delay={0.05}>
        <div className="mt-16">
            <div className="panel rounded-la p-7 sm:p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <div className="max-w-md">
                    <div className="meta-eyebrow">Begin a brief</div>
                    <h2 className="mt-3 font-display font-normal text-[24px] sm:text-[28px] text-la-text leading-snug">
                        Send an enquiry about this service.
                    </h2>
                    <p className="mt-3 text-[13.5px] text-la-text2 leading-relaxed">
                        Your message reaches a senior advisor. We reply within one business day.
                    </p>
                </div>
                <GlowButton as={Link} to={enquiryHref} variant="gold" size="lg" data-testid="service-enquiry-button">
                    <Send className="h-4 w-4" /> Send an enquiry
                </GlowButton>
            </div>
        </div>
    </SectionReveal>
);

const RelatedServiceCard = ({ service }) => (
    <Link
        to={`/services/${service.slug}`}
        className="group relative overflow-hidden rounded-la border border-white/10 bg-la-canvas hover:border-la-gold/40 transition-[border-color,box-shadow] duration-500 hover:shadow-[0_18px_44px_-22px_rgba(201,168,106,0.45)]"
    >
        <div className="relative aspect-[16/10] overflow-hidden bg-la-canvas2">
            <img
                src={service.image}
                alt=""
                aria-hidden
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                style={{ filter: 'saturate(0.78) brightness(0.78) contrast(1.05)' }}
            />
            <div
                aria-hidden
                className="absolute inset-0"
                style={{ background: 'linear-gradient(180deg, rgba(6,8,12,0.25) 0%, rgba(6,8,12,0.92) 100%)' }}
            />
            <span className="absolute top-4 left-4 inline-flex items-center justify-center h-9 w-9 rounded-la-sm border border-la-gold/30 bg-la-canvas/55 backdrop-blur-md text-la-gold">
                <ServiceIcon name={service.icon} className="h-4 w-4" />
            </span>
        </div>
        <div className="p-6">
            <h4 className="font-display font-normal text-[19px] text-la-text leading-snug">{service.name}</h4>
            <span className="mt-4 inline-flex items-center gap-1.5 text-[12px] text-la-text2 group-hover:text-la-text transition-colors">
                Learn more <ArrowRight className="h-3 w-3" />
            </span>
        </div>
    </Link>
);

const RelatedServices = ({ related }) => {
    if (related.length === 0) return null;
    return (
        <div className="mt-24 pt-16 border-t border-white/8">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <h3 className="font-display font-normal text-[24px] sm:text-[28px] text-la-text">Other services</h3>
                <Link to="/#services" className="text-[12.5px] tracking-[0.10em] uppercase text-la-text2 hover:text-la-text underline-grow">View all</Link>
            </div>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
                {related.map((r) => <RelatedServiceCard key={r.slug} service={r} />)}
            </div>
        </div>
    );
};

// --- Main page --------------------------------------------------------------

export default function ServiceDetailPage() {
    const { slug } = useParams();
    const category = findCategory(slug);

    if (!category) return <ServiceNotFound />;

    const enquiryHref = `/contact?topic=${encodeURIComponent(category.topic)}`;
    const related = SERVICE_CATEGORIES.filter((s) => s.slug !== slug).slice(0, 3);

    return (
        <div className="bg-la-canvas text-la-text min-h-screen">
            <Navbar />
            <main className="pb-24">
                <ServiceHero category={category} />
                <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
                    <ServiceBody category={category} />
                    <ServiceEnquiryCTA enquiryHref={enquiryHref} />
                    <RelatedServices related={related} />
                </div>
            </main>
            <Footer />
        </div>
    );
}
